import { KatDataTable } from '@amzn/katal-react';
import {
  cellFormatterConstructor,
  headerFormatterConstructor,
} from 'src/components/common/util/KatTableColumnHelpers';
import { TranslateFn } from 'src/i18next-arb-shim/useTranslation';
import { formatFloat, formatInt } from './formatUtils';

const headerFormatter = headerFormatterConstructor(
  'lui-dashboard-breakdown-header'
);
const cellFormatter = cellFormatterConstructor('lui-dashboard-breakdown-row');

const I18N_FAMILY_EMPTY_PLACEHOLDER = '—';

const formatI18nFamily = (value: string | null | undefined): string => {
  if (!value) return I18N_FAMILY_EMPTY_PLACEHOLDER;
  return value;
};

/**
 * Returns the KatDataTable column configuration for the dashboard breakdown.
 *
 * Column property names map 1:1 to fields on `DashboardBreakdownRecord`,
 * which also matches the `MetricsSortField` enum's string values. That means
 * `e.detail.sortColumn.property` from the table's sort event can be used
 * directly as the wire-level `sortField` value with no translation.
 */
export default function dashboardBreakdownColumnConfig(
  t: TranslateFn
): KatDataTable.ColumnConfiguration[] {
  return [
    {
      label: t('lui-dashboard-breakdown-col-package-key'),
      property: 'packageKey',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-package-key'),
        'packageKey',
        'breakdown-package-key-col'
      ),
      formatter: cellFormatter('packageKey', 'breakdown-package-key-col'),
    },
    {
      label: t('lui-dashboard-breakdown-col-i18n-family'),
      property: 'i18nFamily',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-i18n-family'),
        'i18nFamily',
        'breakdown-i18n-family-col'
      ),
      formatter: cellFormatter(
        'i18nFamily',
        'breakdown-i18n-family-col',
        'ltr',
        formatI18nFamily
      ),
    },
    {
      label: t('lui-dashboard-breakdown-col-total-requests'),
      property: 'totalRequests',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-total-requests'),
        'totalRequests',
        'breakdown-total-requests-col'
      ),
      formatter: cellFormatter(
        'totalRequests',
        'breakdown-total-requests-col',
        'ltr',
        formatInt
      ),
    },
    {
      label: t('lui-dashboard-breakdown-col-in-progress'),
      property: 'inProgress',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-in-progress'),
        'inProgress',
        'breakdown-in-progress-col'
      ),
      formatter: cellFormatter(
        'inProgress',
        'breakdown-in-progress-col',
        'ltr',
        formatInt
      ),
    },
    {
      label: t('lui-dashboard-breakdown-col-completed'),
      property: 'completed',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-completed'),
        'completed',
        'breakdown-completed-col'
      ),
      formatter: cellFormatter(
        'completed',
        'breakdown-completed-col',
        'ltr',
        formatInt
      ),
    },
    {
      label: t('lui-dashboard-breakdown-col-failed'),
      property: 'failed',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-failed'),
        'failed',
        'breakdown-failed-col'
      ),
      formatter: cellFormatter(
        'failed',
        'breakdown-failed-col',
        'ltr',
        formatInt
      ),
    },
    {
      label: t('lui-dashboard-breakdown-col-avg-tat'),
      property: 'averageTurnaroundTime',
      sortable: true,
      headerFormatter: headerFormatter(
        t('lui-dashboard-breakdown-col-avg-tat'),
        'averageTurnaroundTime',
        'breakdown-avg-tat-col'
      ),
      formatter: cellFormatter(
        'averageTurnaroundTime',
        'breakdown-avg-tat-col',
        'ltr',
        (value: number) => formatFloat(value, 1)
      ),
    },
  ];
}
